/**
 * useCamera — React hook for camera access and lifecycle management.
 *
 * Handles getUserMedia, stream attachment, and cleanup.
 * Must be triggered by a user gesture on iOS Safari.
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import type {
  CameraStatus,
  CameraError,
  UseCameraOptions,
  UseCameraReturn,
} from '../detection/types';

const DEFAULT_WIDTH = 1280;
const DEFAULT_HEIGHT = 720;
const DEFAULT_FACING_MODE = 'environment';

export function useCamera(options: UseCameraOptions = {}): UseCameraReturn {
  const {
    facingMode = DEFAULT_FACING_MODE,
    width = DEFAULT_WIDTH,
    height = DEFAULT_HEIGHT,
    autoStart = false,
  } = options;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<CameraStatus>('idle');
  const [error, setError] = useState<CameraError | null>(null);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setStatus('idle');
    setError(null);
  }, []);

  const start = useCallback(async () => {
    // Don't start if already active or requesting
    if (status === 'active' || status === 'requesting') return;

    setStatus('requesting');
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: width },
          height: { ideal: height },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (!videoRef.current) {
        // Video element was removed while waiting for permission
        stream.getTracks().forEach((track) => track.stop());
        setStatus('idle');
        return;
      }

      videoRef.current.srcObject = stream;

      // Wait for video metadata to load
      await new Promise<void>((resolve, reject) => {
        const video = videoRef.current!;
        if (video.readyState >= 1) {
          resolve();
          return;
        }
        const onLoaded = () => {
          video.removeEventListener('loadedmetadata', onLoaded);
          video.removeEventListener('error', onError);
          resolve();
        };
        const onError = () => {
          video.removeEventListener('loadedmetadata', onLoaded);
          video.removeEventListener('error', onError);
          reject(new Error('Video element error'));
        };
        video.addEventListener('loadedmetadata', onLoaded);
        video.addEventListener('error', onError);
      });

      await videoRef.current.play();
      setStatus('active');
    } catch (err) {
      const cameraError: CameraError = {
        name: err instanceof Error ? err.name : 'UnknownError',
        message: err instanceof Error ? err.message : 'Failed to access camera',
      };
      setError(cameraError);
      setStatus('error');

      // Clean up any partial stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    }
  }, [facingMode, width, height, status]);

  // Auto-start if requested
  useEffect(() => {
    if (autoStart) {
      start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  return { videoRef, status, error, start, stop };
}
